import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useEntitlements } from "@/lib/useEntitlements";
import StalenessBannerFR from "@/components/landing-fr/StalenessBannerFR";
import { fr, priceFR, annualNoteFR } from "@/locales/fr";
import { appUrl } from "@/lib/referral";

const APP_SIGNUP = "https://work.voxiumscript.com";

/**
 * French pricing section. Numbers come from the SAME useEntitlements()
 * hook the English page reads — never retyped — so the 13 price strings
 * on this page can never drift from the English/production values. Only
 * the formatting (non-breaking space before "$", French thousands
 * grouping) and the surrounding copy are French-specific.
 */
export const PricingFR = () => {
  const { data, isStale, lastUpdated } = useEntitlements();
  const t = fr.pricing;

  const tiers = t.tiers.map((tier) => {
    const ent = data?.tiers?.[tier.slug] || {};
    const price = ent.price != null ? priceFR(ent.price) : "—";
    const annual =
      ent.annualPrice != null && ent.price != null
        ? annualNoteFR(ent.price, ent.annualPrice)
        : null;
    return { ...tier, price, annual };
  });

  const packs = t.qa.packs.map((pack) => {
    const ent = data?.packs?.[pack.slug] || {};
    const price = ent.price != null ? priceFR(ent.price) : "—";
    return { ...pack, price };
  });

  return (
    <section
      id="pricing-fr"
      className="relative bg-cream w-full px-6 sm:px-10 lg:px-16 pt-28 sm:pt-32 pb-28 sm:pb-32 border-t border-cream-200"
      data-testid="pricing-section-fr"
    >
      <div className="max-w-[78rem] mx-auto">
        <div className="max-w-prose3 mb-16 sm:mb-20">
          <p className="eyebrow text-gold-900 mb-5" data-testid="pricing-eyebrow-fr">
            {t.eyebrow}
          </p>
          <h2 className="display-serif text-nearblack text-4xl sm:text-5xl leading-[1.05] mb-4">
            {t.headline}
          </h2>
          <p
            className="font-body text-sm text-nearblack/70"
            data-testid="pricing-currency-note-fr"
          >
            {t.currencyNote}
          </p>
        </div>

        {isStale && <StalenessBannerFR lastUpdated={lastUpdated} />}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 mb-16 sm:mb-20">
          {tiers.map((tier) => (
            <div
              key={tier.testId}
              className="border-t border-cream-200 pt-8"
              data-testid={tier.testId}
            >
              <div className="flex items-baseline justify-between gap-4 mb-4">
                <h3 className="display-serif text-nearblack text-2xl">{tier.name}</h3>
                <div className="text-right">
                  <span className="display-serif text-nearblack text-2xl">{tier.price}</span>
                  <span className="font-ui text-xs text-nearblack/70 block mt-0.5">
                    {tier.cadence}
                  </span>
                </div>
              </div>
              {tier.annual && (
                <p
                  className="font-body italic text-sm text-gold-900 mb-3"
                  data-testid={`${tier.testId}-annual`}
                >
                  {tier.annual}
                </p>
              )}
              <p className="font-body text-base leading-relaxed text-nearblack/75 max-w-[42ch]">
                {tier.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-16 sm:mb-20" data-testid="qa-followups-section-fr">
          <div className="max-w-prose3 mb-10 sm:mb-12">
            <p className="eyebrow text-gold-900 mb-5" data-testid="qa-followups-eyebrow-fr">
              {t.qa.eyebrow}
            </p>
            <p className="font-body text-base leading-relaxed text-nearblack/75 max-w-[52ch]">
              {t.qa.intro}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
            {packs.map((pack) => (
              <div
                key={pack.testId}
                className="border-t border-cream-200 pt-8"
                data-testid={pack.testId}
              >
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <h3 className="display-serif text-nearblack text-xl">{pack.name}</h3>
                  <span className="display-serif text-nearblack text-xl">{pack.price}</span>
                </div>
                <p className="font-body text-base leading-relaxed text-nearblack/75">
                  {pack.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 border-t border-cream-200 pt-10">
          <p className="font-body italic text-sm text-nearblack/70 max-w-[44ch]" data-testid="pricing-footnote-fr">
            {t.footnote}
          </p>
          <a href={appUrl(APP_SIGNUP, { lang: "fr" })} className="btn-gold self-start sm:self-auto" data-testid="pricing-cta-fr">
            {t.cta}
            <ArrowUpRight size={16} strokeWidth={2} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default PricingFR;
